import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSharedSlip } from 'services/api/sportBetsService';
import { ModernSheet } from './ModernSheet';
import { ResolvedSlip, SlipSheet, resolveSlips } from './PopularSlips';
import styles from './BetTogether.module.css';

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden className={styles.icon}>
    <path
      fill="currentColor"
      d="M18 16.1c-.8 0-1.4.3-2 .8l-7.1-4.2c.1-.2.1-.5.1-.7s0-.5-.1-.7L16 7.2c.5.5 1.2.8 2 .8a3 3 0 1 0-3-3c0 .2 0 .5.1.7L8 9.8A3 3 0 1 0 8 14.2l7.1 4.2c-.1.2-.1.4-.1.6a2.9 2.9 0 1 0 3-2.9z"
    />
  </svg>
);

/** The share link a friend opens: the home page with the code. */
export const sharedSlipLink = (code: string) =>
  `${window.location.origin}/?share=${encodeURIComponent(code)}`;

type Shared = { code: string; slip: ResolvedSlip; missing: number };

/**
 * Modern home: the "Bet together" ribbon. It opens a sheet to enter a
 * friend's sharing code; the slip behind the code (or behind a ?share= link)
 * then opens, ready to go on the bet slip.
 */
export const BetTogether = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [asking, setAsking] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shared, setShared] = useState<Shared | null>(null);

  const load = useCallback(
    async (raw: string) => {
      const typed = raw.replace(/[\s-]/g, '').toUpperCase();
      if (!typed) return false;
      setBusy(true);
      setError('');
      const data = await getSharedSlip(typed);
      setBusy(false);
      const slip = resolveSlips(data, true)[0];
      if (!data || !slip) {
        setError(t('modern_shared_not_found'));
        return false;
      }
      setShared({
        code: data.code,
        slip: { ...slip, label: data.code },
        missing: data.missing + (data.slips[0].legs.length - slip.picks.length),
      });
      return true;
    },
    [t]
  );

  // A shared link: /?share=CODE opens that slip once.
  const linked = params.get('share');
  useEffect(() => {
    if (!linked) return;
    const next = new URLSearchParams(params);
    next.delete('share');
    setParams(next, { replace: true });
    void load(linked).then((ok) => {
      if (!ok) {
        setCode(linked);
        setAsking(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked]);

  const closeAsk = useCallback(() => {
    setAsking(false);
    setError('');
  }, []);
  const closeShared = useCallback(() => setShared(null), []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await load(code)) {
      setAsking(false);
      setCode('');
    }
  };

  const text = t('modern_bet_together_banner');

  return (
    <>
      <button
        type="button"
        className={styles.ribbon}
        onClick={() => setAsking(true)}
      >
        <span className={styles.track} aria-hidden>
          {[0, 1].map((i) => (
            <span key={i} className={styles.item}>
              <ShareIcon />
              {text}
            </span>
          ))}
        </span>
        <span className={styles.srOnly}>{text}</span>
      </button>

      {asking && (
        <ModernSheet title={t('modern_bet_together_title')} close={closeAsk}>
          <form className={styles.form} onSubmit={submit}>
            <p>{t('modern_bet_together_hint')}</p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t('modern_shared_code_placeholder')}
              maxLength={9}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
            {error && <p className={styles.error}>{error}</p>}
            <button type="submit" disabled={busy || code.trim().length < 6}>
              {t('modern_shared_code_load')}
            </button>
          </form>
        </ModernSheet>
      )}

      {shared && (
        <SlipSheet
          slip={shared.slip}
          title={t('modern_shared_slip_title', { code: shared.code })}
          note={
            shared.missing > 0
              ? t('modern_shared_missing', { count: shared.missing })
              : undefined
          }
          close={closeShared}
        />
      )}
    </>
  );
};
